import type { PageContext } from "@nike/web-shell-types";
import { addPageAction } from "../newRelicUtils";
import { PAGE_TYPE_SCHEMAS } from "./pageContextSchemas";

const isEmpty = (value: unknown): boolean => {
  if (value === null || value === undefined || value === ``) return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
};

const BASE_FIELDS = new Set([`pageType`, `pageIdentifier`, `pageTitle`, `version`, `apiReference`]);

const validateNestedKeys = (
  parentKey: string,
  value: unknown,
  expectedKeys: string[],
  missingFields: string[]
): void => {
  if (value === null || value === undefined || typeof value !== `object`) return;
  const obj = value as Record<string, unknown>;
  for (const key of expectedKeys) {
    if (!(key in obj)) {
      missingFields.push(`${parentKey}.${key}`);
    }
  }
};

export const validatePageContextSchema = (context: PageContext): void => {
  try {
    const schema = PAGE_TYPE_SCHEMAS[context.pageType];
    if (!schema) return;

    const expectedFields = schema.fields;
    const pageSpecificKeys = Object.keys(context).filter((key) => !BASE_FIELDS.has(key));
    const expectedSet = new Set(expectedFields);

    const unexpectedFields: string[] = [];
    const missingFields: string[] = [];
    const emptyFields: string[] = [];

    for (const key of pageSpecificKeys) {
      if (!expectedSet.has(key)) {
        unexpectedFields.push(key);
      }
    }

    for (const field of expectedFields) {
      if (!(field in context)) {
        missingFields.push(field);
      } else if (isEmpty(context[field])) {
        emptyFields.push(field);
      }
    }

    // Validate one level down for nested object fields
    if (schema.nested) {
      for (const [field, expectedKeys] of Object.entries(schema.nested)) {
        if (field in context && !isEmpty(context[field])) {
          validateNestedKeys(field, context[field], expectedKeys, missingFields);
        }
      }
    }

    // Validate one level down for array item fields (check first item as representative)
    if (schema.arrayItem) {
      for (const [field, expectedKeys] of Object.entries(schema.arrayItem)) {
        const arr = context[field];
        if (Array.isArray(arr) && arr.length > 0) {
          validateNestedKeys(`${field}[]`, arr[0], expectedKeys, missingFields);
        }
      }
    }

    if (unexpectedFields.length === 0 && missingFields.length === 0 && emptyFields.length === 0) {
      return;
    }

    addPageAction(`WEB_SHELL_CLIENT_PAGE_CONTEXT_SCHEMA_MISMATCH`, {
      pageType: context.pageType,
      pageIdentifier: context.pageIdentifier,
      unexpectedFields: unexpectedFields.join(`,`),
      missingFields: missingFields.join(`,`),
      emptyFields: emptyFields.join(`,`)
    });
  } catch (error) {
    console.warn(`[WebShellClient] pageContext soft validation error:`, error);
  }
};
